import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Collapse } from 'react-collapse';

import {
  Flex,
  Icon,
  ListWithPagination,
  PaginationContext,
  Text,
  Card,
  Devider,
  Spinner,
  Box,
  useScreenResolution,
} from 'dash-ui';
import {
  GuestUser,
  useGetPortfoliosWithReactionsQuery,
  useGetGuestUserReactionsCountQuery,
} from 'gql/generated.graphql';
import PortfolioWithReactionListItem from './components/PortfolioWithReactionListItem/PortfolioWithReactionListItem';

const StyledList = styled(ListWithPagination)`
  & > div:first-child {
    padding: 0;
    border: none;
  }
  & > div:last-child {
    justify-content: center;
  }
`;
const Header = styled(Flex)`
  cursor: pointer;
`;

const HeaderRowContainer = styled(Flex)`
  @media ${({ theme }) => theme.devices.mobile} {
    flex-direction: column;
  }
`;

const MobileText = styled(Text)`
  display: none;
  @media ${({ theme }) => theme.devices.mobile} {
    display: inherit;
  }
`;

const GuestUserListItem = ({
  guestUser,
  activeGuestUsers,
  setActiveGuestUsers,
}: {
  guestUser: GuestUser;
  activeGuestUsers: string;
  setActiveGuestUsers: (value: string) => void;
}) => {
  const { t } = useTranslation(['common', 'pages']);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { setTotal, state, getRefetchProps, setPerPage } =
    useContext(PaginationContext);
  const pagination = getRefetchProps(state);
  const isActive = activeGuestUsers === guestUser._id;

  const { data, loading } = useGetPortfoliosWithReactionsQuery({
    variables: {
      pagination,
      filter: { guestUserId: guestUser._id },
    },
  });
  const { data: dataCount, loading: loadingCount } =
    useGetGuestUserReactionsCountQuery({
      variables: {
        filter: { guestUserId: guestUser._id },
      },
    });

  const { portfolios, totalCount } = useMemo(
    () => ({
      portfolios: data?.getPortfoliosWithReactions.collection || [],
      totalCount: data?.getPortfoliosWithReactions.totalCount || 0,
    }),
    [data]
  );

  useEffect(() => {
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  useEffect(() => {
    setPerPage(12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card mt={2} w="100%" flexDirection="column">
      <Header
        onClick={() => setActiveGuestUsers(!isActive ? guestUser._id : '')}
        px={3}
        w="100%"
        flexDirection="column"
      >
        <Flex
          p={2}
          w="100%"
          flex={1}
          flexDirection="row"
          justifyContent="space-between"
        >
          <Flex>
            <Icon
              mr={2}
              size={24}
              name={isActive ? 'chevronUp' : 'chevronDown'}
            />
          </Flex>
          <HeaderRowContainer flex={1}>
            <Flex flex={2}>
              <Text semibold>{guestUser.fullName}</Text>
            </Flex>
            <Flex flex={2}>
              <Text>{guestUser.email}</Text>
            </Flex>
            <Flex flex={1}>
              <MobileText>{t('common:labels:properties')}:&nbsp;</MobileText>
              <Text semibold={isMobile}>{state.total}</Text>
            </Flex>
            <Flex flex={1}>
              <MobileText>{t('common:labels:reactions')}:&nbsp;</MobileText>
              {!loadingCount ? (
                <Text semibold={isMobile}>
                  {dataCount?.getGuestUserReactionsCount || 0}
                </Text>
              ) : (
                <Spinner size={16} />
              )}
            </Flex>
          </HeaderRowContainer>
        </Flex>
      </Header>
      <Box w="100%">
        <Collapse className="collapse-container" isOpened={isActive}>
          <Devider mt={1} w="100%" />
          <StyledList
            paginationProps={{
              nextButtonLabel: t('common:labels.next'),
              previousButtonLabel: t('common:labels.previous'),
            }}
            listProps={{
              w: '100%',
              loading,
              loadingProps: { bgColor: 'inherit' },
              grid: ['1fr', 256, 256, 256],
              data: portfolios,
              gap: 8,
              my: 3,
              mx: 3,
            }}
          >
            {(item) => <PortfolioWithReactionListItem portfolio={item} />}
          </StyledList>
        </Collapse>
      </Box>
    </Card>
  );
};

export default GuestUserListItem;
